import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PeriodTabs, NotConnected, fmtN, pct, evoCls } from "./common";
import { fmtDT } from "@/constants";

const FLAG_LABELS = { progression: ["Progresse", "bg-emerald-100 text-emerald-700"], recul: ["Recule", "bg-rose-100 text-rose-700"], fort_potentiel: ["Fort potentiel", "bg-amber-100 text-amber-700"], a_gagner: ["À gagner", "bg-sky-100 text-sky-700"] };

function Th({ label, k, sort, setSort, testid }) {
  return (
    <th className="text-left px-2 py-2 cursor-pointer select-none" data-testid={testid} onClick={() => setSort(k)}>
      {label} {sort === k ? "↓" : ""}
    </th>
  );
}

export default function SeoKeywords() {
  const [period, setPeriod] = useState("28");
  const [status, setStatus] = useState(null);
  const [queries, setQueries] = useState(null);
  const [pages, setPages] = useState(null);
  const [sort, setSort] = useState("impressions");
  const [pageDetail, setPageDetail] = useState(null); // {page, rows}
  const [queryDetail, setQueryDetail] = useState(null); // {query, rows, meta}
  const [meta, setMeta] = useState(null); // {start, end, synced_at}
  useEffect(() => { api.get("/seo/gsc/status").then((r) => setStatus(r.data)).catch(() => setStatus({ error: true })); }, []);
  useEffect(() => {
    if (!status?.connected) return;
    setQueries(null); setPages(null); setMeta(null);
    api.get(`/seo/gsc/queries?period=${period}`).then((r) => { setQueries(r.data.rows); setMeta({ start: r.data.start, end: r.data.end, synced_at: r.data.synced_at }); }).catch(() => setQueries([]));
    api.get(`/seo/gsc/pages?period=${period}`).then((r) => setPages(r.data.rows)).catch(() => setPages([]));
  }, [period, status]);
  if (!status) return <p className="text-sm text-slate-400">Chargement…</p>;
  if (status.error) return <p className="text-sm text-rose-600 rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="seo-status-error">Statut Search Console indisponible pour le moment — le reste de l'application fonctionne normalement.</p>;
  if (!status.connected) return <NotConnected />;
  const openPage = (page) => {
    setPageDetail({ page, rows: null });
    api.get(`/seo/gsc/page-queries?page=${encodeURIComponent(page)}&period=${period}`)
      .then((r) => setPageDetail({ page, rows: r.data.rows }))
      .catch(() => setPageDetail({ page, rows: [] }));
  };
  const openQuery = (query) => {
    setQueryDetail({ query, rows: null });
    api.get(`/seo/gsc/query-pages?query=${encodeURIComponent(query)}&period=${period}`)
      .then((r) => setQueryDetail({ query, rows: r.data.rows, meta: { start: r.data.start, end: r.data.end, synced_at: r.data.synced_at } }))
      .catch(() => setQueryDetail({ query, rows: [] }));
  };
  const sorted = [...(queries || [])].sort((a, b) => sort === "position" ? a.position - b.position : b[sort] - a[sort]);
  return (
    <div className="space-y-5" data-testid="seo-keywords">
      <PeriodTabs value={period} onChange={setPeriod} />
      <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <p className="px-4 pt-4 text-sm font-bold">Mots-clés qui apportent de la visibilité</p>
        {meta && (
          <p className="px-4 pt-1 text-xs text-slate-400" data-testid="seo-keywords-dates">
            Période : du {meta.start} au {meta.end} · Dernière synchronisation : {meta.synced_at ? fmtDT(meta.synced_at) : "—"}
          </p>
        )}
        {!queries && <p className="p-4 text-sm text-slate-400">Chargement…</p>}
        {queries && queries.length === 0 && <p className="p-4 text-sm text-slate-500" data-testid="seo-queries-empty">Aucune requête sur la période — synchronisez depuis Paramètres.</p>}
        {sorted.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm mt-2" data-testid="seo-queries-table">
              <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <tr><Th label="Mot-clé" k="key" sort={sort} setSort={setSort} testid="seo-q-sort-key" /><Th label="Clics" k="clicks" sort={sort} setSort={setSort} testid="seo-q-sort-clicks" /><Th label="Impressions" k="impressions" sort={sort} setSort={setSort} testid="seo-q-sort-impressions" /><Th label="CTR" k="ctr" sort={sort} setSort={setSort} testid="seo-q-sort-ctr" /><Th label="Position" k="position" sort={sort} setSort={setSort} testid="seo-q-sort-position" /><th className="px-2 py-2 text-left">Évol.</th><th className="px-2 py-2 text-left">Pages</th></tr>
              </thead>
              <tbody>
                {sorted.slice(0, 100).map((q, i) => (
                  <tr key={q.key} className="border-b border-slate-50" data-testid={`seo-query-${i}`}>
                    <td className="px-2 py-2 font-medium">{q.key}</td>
                    <td className="px-2 py-2 font-mono tabular-nums">{fmtN(q.clicks)}</td>
                    <td className="px-2 py-2 font-mono tabular-nums">{fmtN(q.impressions)}</td>
                    <td className="px-2 py-2 font-mono tabular-nums">{(q.ctr * 100).toFixed(1)} %</td>
                    <td className="px-2 py-2 font-mono tabular-nums">{q.position}</td>
                    <td className={`px-2 py-2 font-mono tabular-nums text-xs font-semibold ${evoCls(q.evo_clicks)}`}>{pct(q.evo_clicks)}</td>
                    <td className="px-2 py-2">
                      <button type="button" data-testid={`seo-query-pages-btn-${i}`} onClick={() => openQuery(q.key)}
                        className="rounded-lg border border-sky-300 bg-sky-50 text-sky-700 px-2 py-1 text-xs font-semibold hover:bg-sky-100 transition-colors whitespace-nowrap">
                        Voir les pages affichées
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <p className="px-4 pt-4 text-sm font-bold">Pages du site</p>
        {!pages && <p className="p-4 text-sm text-slate-400">Chargement…</p>}
        {pages && pages.length === 0 && <p className="p-4 text-sm text-slate-500">Aucune page sur la période.</p>}
        <div className="divide-y divide-slate-100 mt-2">
          {(pages || []).slice(0, 60).map((p, i) => (
            <button key={p.key} type="button" data-testid={`seo-page-${i}`} onClick={() => openPage(p.key)}
              className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium break-all">{p.key.replace("https://www.photobooth54.fr", "") || "/"}</p>
                <p className="text-xs font-mono tabular-nums text-slate-500 shrink-0">{fmtN(p.clicks)} clics · {fmtN(p.impressions)} impr. · pos. {p.position}</p>
              </div>
              {p.flags.length > 0 && (
                <div className="flex gap-1.5 mt-1 flex-wrap">
                  {p.flags.map((f) => (
                    <span key={f} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${FLAG_LABELS[f]?.[1]}`} data-testid={`seo-page-flag-${i}-${f}`}>{FLAG_LABELS[f]?.[0]}</span>
                  ))}
                </div>
              )}
            </button>
          ))}
        </div>
      </section>
      {queryDetail && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="seo-query-modal">
          <button aria-label="Fermer" className="absolute inset-0 bg-slate-900/50" onClick={() => setQueryDetail(null)} data-testid="seo-query-modal-backdrop" />
          <div className="relative w-full sm:max-w-lg max-h-[85vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white p-5 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-heading text-base font-extrabold">« {queryDetail.query} » — pages affichées sur Google</h3>
              <button data-testid="seo-query-modal-close" onClick={() => setQueryDetail(null)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 shrink-0">Fermer</button>
            </div>
            <p className="text-xs text-slate-500">
              URL réellement présentes dans Search Console pour cette recherche (données mesurées, non déduites).{queryDetail.meta ? ` Période du ${queryDetail.meta.start} au ${queryDetail.meta.end} · sync ${queryDetail.meta.synced_at ? fmtDT(queryDetail.meta.synced_at) : "—"}.` : ""}
            </p>
            {!queryDetail.rows && <p className="text-sm text-slate-400">Chargement…</p>}
            {queryDetail.rows && queryDetail.rows.length === 0 && (
              <p className="text-sm text-slate-500" data-testid="seo-query-modal-empty">Données indisponibles pour cette période — cela ne signifie pas que la page n'apparaît jamais sur Google.</p>
            )}
            {(queryDetail.rows || []).map((r, i) => (
              <div key={i} className="rounded-lg border border-slate-100 p-3 space-y-1" data-testid={`seo-query-page-${i}`}>
                <a href={r.page} target="_blank" rel="noreferrer" data-testid={`seo-query-page-url-${i}`} className="text-sm font-semibold text-sky-700 hover:underline break-all">
                  {r.page.replace("https://www.photobooth54.fr", "") || "/"}
                </a>
                <p className="text-xs font-mono tabular-nums text-slate-500">{fmtN(r.clicks)} clics · {fmtN(r.impressions)} impr. · CTR {(r.ctr * 100).toFixed(1)} % · pos. {r.position}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {pageDetail && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="seo-page-modal">
          <button aria-label="Fermer" className="absolute inset-0 bg-slate-900/50" onClick={() => setPageDetail(null)} data-testid="seo-page-modal-backdrop" />
          <div className="relative w-full sm:max-w-lg max-h-[85vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white p-5 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-heading text-base font-extrabold break-all">{pageDetail.page.replace("https://www.photobooth54.fr", "") || "/"}</h3>
              <button data-testid="seo-page-modal-close" onClick={() => setPageDetail(null)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 shrink-0">Fermer</button>
            </div>
            <p className="text-xs text-slate-500">Principales requêtes Google de cette page</p>
            {!pageDetail.rows && <p className="text-sm text-slate-400">Chargement…</p>}
            {pageDetail.rows && pageDetail.rows.length === 0 && <p className="text-sm text-slate-500">Aucune requête mesurée pour cette page sur la période.</p>}
            {(pageDetail.rows || []).slice(0, 30).map((q, i) => (
              <div key={i} className="flex justify-between gap-2 text-sm border-b border-slate-50 pb-1.5" data-testid={`seo-page-query-${i}`}>
                <span className="font-medium">{q.key}</span>
                <span className="text-xs font-mono tabular-nums text-slate-500 shrink-0">{fmtN(q.clicks)} clics · {fmtN(q.impressions)} impr. · pos. {q.position}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
